import { CONTENT_BASE } from "../config";
import type { Module } from "../types";

// Registro de módulos por sala (coincide con el campo `room` de cada JSON).
export const ROOMS: Record<string, string[]> = {
  elemental: ["matematicas", "espanol", "ciencias", "estudios-sociales", "historia-pr", "ingles"],
  // Los cursos STEM (ciencia interactiva, programación, retos, robótica) son
  // lecciones, no juegos. Son electivos que cruzan grados (crossGrade): se
  // listan en Intermedia Y Superior, al final (order 1001+), y no participan
  // del filtro de grado de la sala.
  intermedia: ["matematicas-intermedia", "ciencias-intermedia", "espanol-intermedia", "historia-pr-intermedia", "ingles-intermedia", "estudios-intermedia", "ciencia-interactiva", "programacion", "retos-matematicos", "robotica"],
  superior: ["matematicas-superior", "biologia-superior", "espanol-superior", "ingles-superior", "historia-superior", "ciencia-interactiva", "programacion", "retos-matematicos", "robotica"],
  // La sala STEM ya no tiene módulos-curso: solo juegos y simulaciones (Juegos).
  stem: [],
  ia: ["ia-ninos", "ia-jovenes", "ia-adultos"],
};

// Todos los slugs conocidos del piloto (coinciden con el manifest de la API).
export const MODULE_SLUGS = Object.values(ROOMS).flat();

/**
 * ¿El módulo `slug` se sirve DENTRO de la sala `room`? La ruta `/:room/:slug`
 * solo es legítima para los pares que registra ROOMS.
 *
 * Antes la guarda miraba la sala y SubjectDetail cargaba cualquier slug: el
 * kiosco abría `#/stem/matematicas` (su sala `stem` no tiene módulos) y un
 * participante `#/cualquiera/matematicas-superior` (una sala desconocida no es
 * "controlable" y pasaba). La URL decidía qué contenido se veía, no el acceso.
 *
 * Finanzas NO está aquí a propósito: sus módulos se abren por `/finanzas`
 * (con `?grado=` y `?leccion=`), no por esta ruta — ver `rutaDeLeccion`.
 */
export function moduloEnSala(room: string, slug: string): boolean {
  return Object.prototype.hasOwnProperty.call(ROOMS, room) && ROOMS[room].includes(slug);
}

/*
 * Los módulos de Educación Financiera. Viven fuera de ROOMS porque no se
 * listan en ninguna sala: los carga la página `/finanzas` según el grado
 * elegido. Espejo de `GENERAL`/`GRADOS` en pages/Finanzas.tsx — si se añade una
 * banda allí, se añade aquí.
 */
const FINANZAS_SLUGS = ["finanzas", "finanzas-k3", "finanzas-45", "finanzas-68", "finanzas-912"];

/** La clave `?grado=` de Finanzas para una lección: "Kínder" -> kinder, "4.º" -> g4. */
function claveGradoFinanzas(grade: string | undefined): string | null {
  if (!grade || grade === "Todos") return null;
  if (grade === "Kínder") return "kinder";
  const n = Number.parseInt(grade, 10);
  return Number.isFinite(n) ? `g${n}` : null;
}

/**
 * El enlace (hash) que abre una lección concreta, o `undefined` si el módulo ya
 * no se sirve en ninguna parte (no se pinta un enlace roto).
 *
 * Existe porque "Mi perfil" solo buscaba en ROOMS y el avance de Finanzas
 * desaparecía de "recientes" sin decir nada.
 */
export function rutaDeLeccion(slug: string, lessonId: string, grade?: string): string | undefined {
  const id = encodeURIComponent(lessonId);
  if (FINANZAS_SLUGS.includes(slug)) {
    // El módulo general no lleva grado; las bandas necesitan `?grado=` para que
    // Finanzas cargue el módulo correcto (a un participante lo fija su grado).
    const clave = slug === "finanzas" ? null : claveGradoFinanzas(grade);
    return clave ? `#/finanzas?grado=${clave}&leccion=${id}` : `#/finanzas?leccion=${id}`;
  }
  const room = Object.keys(ROOMS).find((r) => ROOMS[r].includes(slug));
  return room ? `#/${room}/${slug}?leccion=${id}` : undefined;
}

const cache = new Map<string, Module>();

export async function loadModule(slug: string): Promise<Module> {
  if (cache.has(slug)) return cache.get(slug)!;
  const res = await fetch(`${CONTENT_BASE}/modules/${slug}.json`);
  if (!res.ok) throw new Error(`No se pudo cargar el módulo ${slug} (${res.status})`);
  const mod = (await res.json()) as Module;
  cache.set(slug, mod);
  return mod;
}

export async function loadModulesForRoom(room: string): Promise<Module[]> {
  const slugs = ROOMS[room] ?? [];
  const mods = await Promise.all(slugs.map((s) => loadModule(s)));
  // Orden explícito (order) si está presente; si no, alfabético por subject.
  return mods.sort((a, b) => {
    const ao = a.order ?? 999;
    const bo = b.order ?? 999;
    if (ao !== bo) return ao - bo;
    return a.subject.localeCompare(b.subject, "es");
  });
}
